//#region IMPORTS
import { InstoreTemplateRow } from "../../models/instoreTemplate";
import { PlusCircleIcon } from "@heroicons/react/24/outline";
import { useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
//#endregion

//#region INTERFACE
interface Props {
  items: InstoreTemplateRow[];
  quantityByRowId: Map<number, number>;
  onRowAdd: (row: InstoreTemplateRow, quantity: number) => void;
  disabled?: boolean;
  showStoreFormat?: boolean;
}
//#endregion

export default function WeekOrderThemeItems({
  items,
  quantityByRowId,
  onRowAdd,
  disabled = false,
  showStoreFormat = false,
}: Props) {
  //#region SETUP
  const containerRef = useRef<HTMLDivElement>(null);
  const [qtyOverrides, setQtyOverrides] = useState<Record<number, number>>({});

  const virtualizer = useVirtualizer({
    getScrollElement: () => containerRef.current,
    estimateSize: () => 48,
    count: items.length,
    overscan: 6,
  });

  const getQty = (row: InstoreTemplateRow) =>
    qtyOverrides[row.id] ?? row.amount;
  //#endregion

  //#region RENDER
  return (
    <div
      ref={containerRef}
      className="border-t border-gray-100 max-h-[60vh] overflow-auto"
    >
      <ul
        className="relative"
        style={{ height: `${virtualizer.getTotalSize()}px` }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const row = items[virtualRow.index];
          const inBasket = quantityByRowId.get(row.id) ?? 0;
          const qty = getQty(row);
          const addDisabled = disabled || !qty || qty < 1;
          return (
            <li
              key={row.id}
              className="absolute left-0 right-0 flex items-center justify-between gap-4 px-6 border-b border-gray-100"
              style={{
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <div className="min-w-0 flex flex-col">
                <div className="flex items-center gap-3 min-w-0">
                  <p className="truncate text-base font-semibold text-gray-900">
                    {row.article_name || "—"}
                  </p>
                  {inBasket > 0 && (
                    <span className="shrink-0 inline-flex items-center justify-center rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold h-6 min-w-6 px-2">
                      {inBasket}
                    </span>
                  )}
                </div>
                {showStoreFormat && row.store_format && (
                  <p className="truncate text-sm text-gray-500">
                    {row.store_format}
                  </p>
                )}
              </div>
              <div className="shrink-0 flex items-center gap-3">
                <input
                  type="number"
                  min={1}
                  value={qty}
                  disabled={disabled}
                  onChange={(e) =>
                    setQtyOverrides((prev) => ({
                      ...prev,
                      [row.id]: Number(e.target.value),
                    }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !addDisabled) {
                      e.preventDefault();
                      onRowAdd(row, qty);
                      setQtyOverrides((prev) => {
                        const { [row.id]: _, ...rest } = prev;
                        return rest;
                      });
                    }
                  }}
                  className="w-20 rounded-md border-0 py-1 px-2 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-purple-600 disabled:bg-gray-50 disabled:cursor-not-allowed"
                />
                <button
                  type="button"
                  disabled={addDisabled}
                  onClick={() => {
                    onRowAdd(row, qty);
                    setQtyOverrides((prev) => {
                      const { [row.id]: _, ...rest } = prev;
                      return rest;
                    });
                  }}
                  className="inline-flex items-center gap-1.5 rounded-md bg-gray-200 px-3 py-1 text-sm font-semibold text-gray-800 shadow-sm hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  <PlusCircleIcon className="h-4 w-4" aria-hidden="true" />
                  Add
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
  //#endregion
}
